/**
 * Offline support - lets the app keep working without a connection and syncs
 * both ways when the connection is back.
 *
 * Works underneath every API request (it wraps window.fetch, like
 * utils/syncGuard.js), so pages and API helpers don't need to know about it:
 *
 * - GET responses are cached (offlineStorage.js). Offline, the cached
 *   response is returned, with the changes made offline applied to it.
 * - Creates, updates, deletes and reorders of tasks, boards, lists, notes and
 *   post-its made offline are queued and answered with the result the server
 *   would give, so the page updates right away. New documents get their IDs
 *   on the device (offlineResources.js), so later changes can refer to them.
 * - Back online, the queued changes are sent in order ("Syncing" overlay),
 *   with the values they were based on, so the server merges them with
 *   changes made elsewhere in the meantime (server/utils/revision.js). Then
 *   all pages reload their data from the server.
 *
 * Anything else (uploads, settings, chat, billing, ...) needs a connection and
 * gets a 503 "Not available offline" response while offline.
 */
import {
  clearStoredResponses,
  loadStoredData,
  removeOp,
  storeOp,
  storeResponse,
} from "./offlineStorage";
import {
  OBJECT_ID,
  REVISIONED,
  applyPendingChanges,
  changedFields,
  collectEntities,
  describeMutation,
  reorderEntries,
  synthesizeCreate,
  withItemIds,
  withNewIds,
} from "./offlineResources";
import { requestResync } from "../utils/syncGuard";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:5001";
const STATUS_EVENT = "flowmind:offline-status";
const PROBE_INTERVAL = 15000; // How often to check whether we're back online
const MIN_SYNC_OVERLAY_TIME = 800; // Don't let the "Syncing" overlay just flash
const RETRY_DELAY = 10000; // Wait before retrying a sync the server failed
const MAX_ATTEMPTS = 3; // Server errors before a queued change is given up

// GET responses that are not cached
const NOT_CACHED = ["/api/stripe", "/api/superadmin", "/api/proxy-image"];

// Which cached GET responses a change to a resource can show up in
const RELATED_PATHS = {
  tasks: ["/api/tasks"],
  sections: ["/api/tasks"],
  boards: ["/api/boards"],
  notes: ["/api/notes"],
  noteGroups: ["/api/notes"],
  todolists: ["/api/todolists"],
  postits: ["/api/postits"],
};

let nativeFetch = null;
let ready = Promise.resolve();
let responses = new Map(); // cache key -> response JSON (server state)
let entities = new Map(); // _id -> document, from the cached responses
let outbox = []; // Queued changes, oldest first
let lastSeq = 0;
let syncPromise = null;
let lastSyncFailedAt = 0;
let retryTimer = null;
let probeTimer = null;
let authStorageKey = "auth-storage"; // Where the auth store keeps the login

const state = { online: navigator.onLine, syncing: false, failed: 0 };
let status = { online: state.online, syncing: false, pending: 0, failed: 0 };

// ---- Status (for the UI) ----

const emitStatus = () => {
  status = {
    online: state.online,
    syncing: state.syncing,
    pending: outbox.length,
    failed: state.failed,
  };
  window.dispatchEvent(new CustomEvent(STATUS_EVENT));
};

/** Current status: { online, syncing, pending, failed } */
export const getOfflineStatus = () => status;

export const subscribeOfflineStatus = (callback) => {
  window.addEventListener(STATUS_EVENT, callback);
  return () => window.removeEventListener(STATUS_EVENT, callback);
};

// ---- Helpers ----

const isEqual = (a, b) => JSON.stringify(a) === JSON.stringify(b);
const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const getAuth = () => {
  try {
    return JSON.parse(localStorage.getItem(authStorageKey))?.state || {};
  } catch {
    return {};
  }
};

const getUserId = () => {
  const { user } = getAuth();
  return user?._id || user?.id || null;
};

// fetch() rejects with a TypeError when the server can't be reached
const isNetworkError = (err) => err instanceof TypeError;

const apiOrigin = new URL(API_URL, window.location.origin).origin;

const isApiUrl = (url) =>
  url.pathname.startsWith("/api/") &&
  (url.origin === apiOrigin || url.origin === window.location.origin);

const cacheKey = (url) => {
  const params = new URLSearchParams(url.search);
  params.delete("clientId");
  const search = params.toString();
  return search ? `${url.pathname}?${search}` : url.pathname;
};

const jsonResponse = (data, status = 200) =>
  new Response(JSON.stringify(data), {
    status,
    headers: { "Content-Type": "application/json", "X-Offline": "true" },
  });

const unavailableOffline = () =>
  jsonResponse({ error: "offline", message: "Not available offline" }, 503);

const isJsonResponse = (response) =>
  (response.headers.get("Content-Type") || "").includes("application/json");

const parseBody = (body) => {
  if (typeof body !== "string") return undefined;
  try {
    return JSON.parse(body);
  } catch {
    return undefined;
  }
};

// ---- Online/offline detection ----

const probe = async () => {
  try {
    const response = await nativeFetch(`${API_URL}/ping`, {
      cache: "no-store",
    });
    if (response.ok) goOnline();
  } catch {
    // Still offline
  }
};

const goOffline = () => {
  if (!probeTimer) probeTimer = setInterval(probe, PROBE_INTERVAL);
  if (!state.online) return;
  state.online = false;
  emitStatus();
};

const goOnline = () => {
  clearInterval(probeTimer);
  probeTimer = null;
  if (state.online) return;
  state.online = true;
  emitStatus();
  if (outbox.length) {
    syncNow(true);
  } else {
    // Load what changed on other devices while we were offline
    requestResync("reconnect");
  }
};

// ---- Cache ----

// Documents known from the server, plus those created offline
const knownEntities = () => {
  const known = new Map(entities);
  outbox.forEach((op) => {
    if (op.kind === "create") known.set(op.doc._id, op.doc);
  });
  return known;
};

// Apply a change the server has accepted to the cached responses, so they
// stay right if we go offline before the page loads them again
const applyToCache = (op) => {
  const paths = RELATED_PATHS[op.resource] || [];
  responses.forEach((data, key) => {
    if (!paths.some((path) => key.startsWith(path))) return;
    const url = new URL(key, API_URL);
    const updated = applyPendingChanges(structuredClone(data), url, [op]);
    if (!isEqual(updated, data)) {
      responses.set(key, updated);
      storeResponse(key, updated);
    }
  });
  if (op.kind === "create") collectEntities(op.doc, entities);
  if (op.kind === "update") collectEntities(op.body, entities);
};

// The change as the server made it: for creates and updates, the server's
// response if it is the document
const acceptedChange = (op, responseData) => {
  const isDocument =
    responseData?._id &&
    (op.kind === "create" || String(responseData._id) === op.id);
  if (op.kind === "create") {
    return { ...op, doc: isDocument ? responseData : op.doc || op.body };
  }
  if (op.kind === "update" && isDocument) return { ...op, body: responseData };
  return op;
};

// A document as it is on this device: the server's version with the queued
// changes applied (or the document created offline)
const localEntity = (id) => {
  let entity = entities.has(id) ? structuredClone(entities.get(id)) : null;
  outbox.forEach((op) => {
    if (op.kind === "create" && op.doc._id === id) {
      entity = structuredClone(op.doc);
    } else if (op.kind === "update" && op.id === id && entity) {
      Object.assign(entity, changedFields(op.body));
    } else if (op.kind === "reorder" && entity) {
      reorderEntries(op.body).forEach(({ _id, ...fields }) => {
        if (_id === id) Object.assign(entity, fields);
      });
    } else if (op.kind === "delete" && op.id === id) {
      entity = null;
    }
  });
  return entity;
};

// Tasks show who is responsible as a user object - fill it in when a task
// was given a responsible user (ID) offline
const withResponsibleUser = (task) => {
  if (
    typeof task?.responsible !== "string" ||
    !OBJECT_ID.test(task.responsible)
  ) {
    return task;
  }
  const user = [...entities.values()].find(
    (e) => e._id === task.responsible && (e.firstName || e.name),
  );
  if (!user) return task;
  return {
    ...task,
    responsible: {
      _id: user._id,
      name: user.name || `${user.firstName} ${user.lastName || ""}`.trim(),
      email: user.email,
      profile: {
        color: user.profile?.color || "#43c6c6",
        useGravatar: user.profile?.useGravatar || false,
      },
    },
  };
};

// ---- Requests ----

const handleGet = async (input, init, url) => {
  const key = cacheKey(url);
  const cacheable = !NOT_CACHED.some((path) => url.pathname.startsWith(path));

  if (state.online) {
    // Send changes made offline before loading data that may depend on them
    if (outbox.length) await syncNow();
    try {
      const response = await nativeFetch(input, init);
      if (!response.ok || !cacheable || !isJsonResponse(response)) {
        return response;
      }
      const data = await response.clone().json();
      responses.set(key, data);
      collectEntities(data, entities);
      storeResponse(key, data);
      if (outbox.length) {
        return jsonResponse(
          applyPendingChanges(structuredClone(data), url, outbox),
          response.status,
        );
      }
      return response;
    } catch (err) {
      if (!isNetworkError(err)) throw err;
      goOffline();
    }
  }

  if (responses.has(key)) {
    return jsonResponse(
      applyPendingChanges(structuredClone(responses.get(key)), url, outbox),
    );
  }
  // A single document known from other responses, or created offline
  const lastPart = url.pathname.split("/").pop();
  if (OBJECT_ID.test(lastPart)) {
    const entity = localEntity(lastPart);
    if (entity) return jsonResponse(entity);
  }
  return unavailableOffline();
};

const addOp = (op) => {
  lastSeq = Math.max(Date.now(), lastSeq + 1);
  const queued = { ...op, seq: lastSeq, userId: getUserId(), attempts: 0 };
  outbox.push(queued);
  storeOp(queued);
  emitStatus();
};

const removeOps = (predicate) => {
  outbox = outbox.filter((op) => {
    if (!predicate(op)) return true;
    removeOp(op.seq);
    return false;
  });
};

// Queue a change and answer the way the server would
const queueChange = (mutation, url, body) => {
  const { kind, resource, id } = mutation;
  const path = url.pathname;
  const createdOffline = outbox.some(
    (op) => op.kind === "create" && op.doc._id === id,
  );

  if (kind === "create") {
    const doc = synthesizeCreate(resource, body, {
      entities: knownEntities(),
      userId: getUserId(),
    });
    addOp({ kind, resource, path, method: "POST", body, doc });
    return jsonResponse(doc, 201);
  }

  if (kind === "update") {
    const changes = withItemIds(body || {});
    const serverVersion = createdOffline ? null : entities.get(id);
    const existing = outbox.find((op) => op.kind === "update" && op.id === id);

    // The server's values of the changed fields, for merging when syncing
    const base = { ...(existing?.base || {}) };
    if (serverVersion) {
      Object.keys(changes).forEach((key) => {
        if (key !== "rev" && !(key in base) && key in serverVersion) {
          base[key] = serverVersion[key];
        }
      });
    }

    if (existing) {
      Object.assign(existing.body, changes);
      existing.base = base;
      storeOp(existing);
      emitStatus();
    } else {
      addOp({
        kind,
        resource,
        id,
        path,
        method: "PUT",
        body: changes,
        base,
        baseRev: serverVersion?.rev,
      });
    }
    const entity = localEntity(id) || { _id: id, ...changes };
    return jsonResponse(
      resource === "tasks" ? withResponsibleUser(entity) : entity,
    );
  }

  if (kind === "delete") {
    if (createdOffline) {
      // Never reached the server - just forget about it
      removeOps((op) => op.doc?._id === id || op.id === id);
      emitStatus();
    } else {
      removeOps((op) => op.kind === "update" && op.id === id);
      addOp({ kind, resource, id, path, method: "DELETE" });
    }
    return jsonResponse({ message: "Deleted" });
  }

  // Reorder
  addOp({ kind, resource, path, method: "PUT", body });
  return jsonResponse({ message: "Reordered" });
};

const handleMutation = async (input, init, url, method) => {
  const mutation = describeMutation(method, url.pathname);

  const hasOtherBody = init.body != null && typeof init.body !== "string";
  if (!mutation || hasOtherBody) {
    // Needs a connection (uploads, settings, ...)
    if (!state.online) return unavailableOffline();
    try {
      return await nativeFetch(input, init);
    } catch (err) {
      if (isNetworkError(err)) goOffline();
      throw err;
    }
  }

  let body = parseBody(init.body);
  let sendInit = init;
  if (mutation.kind === "create") {
    body = withNewIds(mutation.resource, body);
    sendInit = { ...init, body: JSON.stringify(body) };
  }

  // While changes are queued, new ones queue behind them to keep their order
  if (state.online && !outbox.length) {
    try {
      const response = await nativeFetch(input, sendInit);
      if (response.ok && isJsonResponse(response)) {
        const data = await response
          .clone()
          .json()
          .catch(() => null);
        applyToCache(acceptedChange({ ...mutation, body }, data));
      }
      return response;
    } catch (err) {
      if (!isNetworkError(err)) throw err;
      goOffline();
    }
  }

  return queueChange(mutation, url, body);
};

const offlineFetch = async (input, init = {}) => {
  if (typeof input !== "string" && !(input instanceof URL)) {
    return nativeFetch(input, init);
  }
  const url = new URL(String(input), window.location.origin);
  if (!isApiUrl(url)) return nativeFetch(input, init);

  await ready;
  const method = (init.method || "GET").toUpperCase();
  return method === "GET"
    ? handleGet(input, init, url)
    : handleMutation(input, init, url, method);
};

// ---- Sync ----

// Send one queued change. Returns "done", "failed" (rejected by the server,
// given up), "skipped", "retry" (couldn't be sent now) or "offline"
const sendOp = async (op) => {
  // Wait until someone is logged in; skip changes queued by someone else who
  // was logged in on this device
  const userId = getUserId();
  if (!userId) return "retry";
  if (op.userId && op.userId !== userId) return "skipped";

  let body = op.body;
  if (op.kind === "update") {
    // Only send what was actually changed offline
    const base = op.base || {};
    const changed = Object.fromEntries(
      Object.entries(body).filter(
        ([key, value]) =>
          key === "rev" || !(key in base) || !isEqual(value, base[key]),
      ),
    );
    if (!Object.keys(changed).some((key) => key !== "rev")) return "done";
    body = changed;
    if (REVISIONED.has(op.resource) && Object.keys(base).length) {
      body = {
        ...body,
        rev: op.baseRev ?? body.rev ?? 0,
        offlineBase: Object.fromEntries(
          Object.keys(changed)
            .filter((key) => key in base)
            .map((key) => [key, base[key]]),
        ),
      };
    }
  }

  const { token } = getAuth();
  try {
    const response = await nativeFetch(`${API_URL}${op.path}`, {
      method: op.method,
      headers: {
        ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });

    if (response.ok) {
      const data = isJsonResponse(response)
        ? await response.json().catch(() => null)
        : null;
      applyToCache(acceptedChange({ ...op, body }, data));
      return "done";
    }
    // Deleted on another device in the meantime - nothing left to change
    if (response.status === 404) return "done";
    // Logged out or overloaded: try again later
    if (response.status === 401 || response.status === 429) return "retry";
    if (response.status >= 500) {
      op.attempts = (op.attempts || 0) + 1;
      storeOp(op);
      if (op.attempts < MAX_ATTEMPTS) return "retry";
    }
    console.error(
      `Offline change rejected by the server (${response.status}):`,
      op.method,
      op.path,
    );
    return "failed";
  } catch (err) {
    if (!isNetworkError(err)) throw err;
    goOffline();
    return "offline";
  }
};

/**
 * Send the queued changes now (if online)
 * @param {Boolean} [force] - Also right after a failed attempt (e.g. when the
 *   connection is back)
 */
export const syncNow = (force = false) => {
  if (syncPromise) return syncPromise;
  if (!outbox.length || !state.online) return Promise.resolve();
  // Sent once someone is logged in again (pages load data after login)
  if (!getUserId()) return Promise.resolve();
  if (!force && Date.now() - lastSyncFailedAt < RETRY_DELAY) {
    return Promise.resolve();
  }
  clearTimeout(retryTimer);
  // Retries after a failed attempt run in the background, without the overlay
  const showOverlay = force || !lastSyncFailedAt;

  syncPromise = (async () => {
    state.syncing = showOverlay;
    state.failed = 0;
    emitStatus();
    const startedAt = Date.now();
    let finished = true;

    while (outbox.length) {
      const op = outbox[0];
      const result = await sendOp(op).catch((err) => {
        console.error("Failed to send offline change:", err);
        return "retry";
      });
      if (result === "offline") {
        // Will be sent when the connection is back (see goOnline)
        finished = false;
        break;
      }
      if (result === "retry") {
        finished = false;
        lastSyncFailedAt = Date.now();
        retryTimer = setTimeout(syncNow, RETRY_DELAY);
        break;
      }
      if (result === "failed") state.failed += 1;
      outbox = outbox.filter((queued) => queued !== op);
      removeOp(op.seq);
      emitStatus();
    }

    if (finished) lastSyncFailedAt = 0;
    const elapsed = Date.now() - startedAt;
    if (showOverlay && state.online && elapsed < MIN_SYNC_OVERLAY_TIME) {
      await wait(MIN_SYNC_OVERLAY_TIME - elapsed);
    }
    state.syncing = false;
    emitStatus();

    // Load the result, including what changed on other devices
    if (finished) requestResync("reconnect");
  })().finally(() => {
    syncPromise = null;
  });
  return syncPromise;
};

/**
 * Forget the cached data (on logout). Queued changes are kept, so they are
 * still sent if the user was logged out because their login expired while
 * offline - they are only ever sent for the user who made them.
 */
export const clearOfflineData = () => {
  responses = new Map();
  entities = new Map();
  state.failed = 0;
  clearStoredResponses();
  emitStatus();
};

/**
 * @param {Object} [options]
 * @param {String} [options.authKey] - localStorage key of the persisted auth
 *   store (for the logged-in user and token)
 */
export const installOfflineSupport = ({ authKey = "auth-storage" } = {}) => {
  if (window.__flowmindOfflineInstalled) return;
  window.__flowmindOfflineInstalled = true;
  authStorageKey = authKey;

  nativeFetch = window.fetch.bind(window);
  window.fetch = offlineFetch;

  ready = loadStoredData().then((stored) => {
    responses = stored.responses;
    outbox = stored.outbox;
    lastSeq = outbox.length ? outbox[outbox.length - 1].seq : 0;
    responses.forEach((data) => collectEntities(data, entities));
    emitStatus();
  });

  ready.then(() => {
    if (!state.online) goOffline();
    else if (outbox.length) syncNow();
  });

  window.addEventListener("online", probe);
  window.addEventListener("offline", goOffline);
};
